import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CRIME_TYPES } from '../../crimeTypes';
import { DEFAULT_AMENITY_CATEGORIES, DEFAULT_AMENITY_SUBCATEGORIES } from '../../amenityTypes';

const initialState = {
    userLocation:{     
        place: '',   
        //point:{x: -15669.585258727895, y: 6709489.671562576}
        point: {x: null, y: null},
        // The name the place search shows for the point, as {name, x, y}: the place
        // picked, or the nearest street or postcode (/nearplace) for a spot chosen on
        // the map. Tied to the point it names, so a stale name is never shown.
        name: {name:'', x:null, y:null}
    },
    userOptions: {
        selectedLayers: [],
        dist_m:1207
    },
    uiState: {
        // Closed at first: the place search is in the top bar, so the menu is not needed to start
        mainMenuOpen:false,
        filterMenuOpen:false,
        shadeByLayer:'',
        shadeBy:'',
        shadeByOptions:null,
        alertText:'',
        // The crime types shown on the crime locations layer
        crimeTypes:CRIME_TYPES,
        schoolType:'all',
        transportType:'all',
        // The amenity categories, and the subcategories within them, shown on the amenities layer
        amenityCategories:DEFAULT_AMENITY_CATEGORIES,
        amenitySubcategories:DEFAULT_AMENITY_SUBCATEGORIES,
        propertyType:'all',
        propertyMaxMonths:'15',
        graphType:'',
        placeDetails:{displayname:'', location:'', ladcode:'', ladname:'', wardcode:'', wardname:'', 
                        pconcode:'', pconname:'', pfacode:'', pfaname:'',
                        regioncode:'', regionname:'', countrycode:'', countryname:'', dataissues:{}},
        isMobile:false,
        awaitingRecenter:false,
        warnings:{Crime:[], General:[]},
        info:{General:[]},
        help:{General:[]},
        controlsVisible:true,
        graphDialogOpen:false,
        shadingValue:'none',
        clickPoint:{x:null, y:null},   
        initClickPoint:{x:null, y:null},
        layerLoadCount:0,
        sideDrawerOpen:false,
        top10Order:null,
        top10Category:null,
        top10Params:null, 
        showOverview: false,
        areaTypes: ['local'],
        propTypes: ['All'],
        metric: 'medpricepersqm',
        showListDialog: false,
        propertyOaPeriod: 'full',
        propertyOaPropType: 'all',
        propertyOaPropTypeOptions: null,
        // What the Deprivation layer ranks each neighbourhood against: 'country' (all of
        // England) or 'lad' (its own local authority), from ukmap.imd details
        imdCompare: 'country',
        // The Big Picture area maps' settings, by layer (AreaMapLayer.js) - e.g.
        // {propertyLad: {metric, period, category}}; a layer's defaults fill any gap
        areaMapOptions: {},
        // The layers that were on before a Big Picture map was opened, put back when it is closed
        areaMapReturnLayers: null,
        // A local layer picked from the layer menus before a location was chosen
        // ({name, type, text, phrase}, LayerGroupsMapControl.js): it asks for a location,
        // and the layer goes on once one is picked
        pickLocationFor: null,
        deg:2,
        // The focus area size the server chose for the current location (see PointCircleLayer)
        defaultDeg:null,
    }
  };

export const mapSlice = createSlice({
    name: 'map',
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        //setMap: (state, action) => {state.map = action.payload},
        //setPopupOverlay: (state, action) => {state.popupOverlay = action.payload},
        //setPopupContent: (state, action) => {state.popupContent = action.payload},
        setPlace: (state, action) => {state.userLocation.place = action.payload.toUpperCase()},
        setDist_m: (state, action) => {state.userOptions.dist_m = parseInt(action.payload)},
        setPoint: (state, action) => {state.userLocation.point = action.payload},   
        setLocationName: (state, action) => {state.userLocation.name = action.payload},
        // The clear button in the place search: no point, so a new one can be chosen
        clearLocation: (state) => {
            state.userLocation = initialState.userLocation;
            state.uiState.clickPoint = initialState.uiState.clickPoint;
            state.uiState.initClickPoint = initialState.uiState.initClickPoint;
            state.uiState.placeDetails = initialState.uiState.placeDetails;
            state.uiState.showOverview = false;
        },
        setSelectedLayers: (state, action) => {state.userOptions.selectedLayers = action.payload},
        setMainMenuOpen: (state, action) => {state.uiState.mainMenuOpen = action.payload},
        toggleMainMenuOpen: (state, action) => {state.uiState.mainMenuOpen = !state.uiState.mainMenuOpen},
        setAlertText: (state, action) => {state.uiState.alertText = action.payload},
        setCrimeTypes: (state, action) => {state.uiState.crimeTypes = action.payload},
        setSchoolType: (state, action) => {state.uiState.schoolType = action.payload},
        setTransportType: (state, action) => {state.uiState.transportType = action.payload},
        // {categories, subcategories}
        setAmenityFilter: (state, action) => {
            state.uiState.amenityCategories = action.payload.categories;
            state.uiState.amenitySubcategories = action.payload.subcategories;
        },
        setPropertyType: (state, action) => {state.uiState.propertyType = action.payload},
        setPropertyMaxMonths: (state, action) => {state.uiState.propertyMaxMonths = action.payload},
        setGraphType: (state, action) => {state.uiState.graphType = action.payload},
        setPlaceDetails: (state, action) => {state.uiState.placeDetails = action.payload},      
        setFilterMenuOpen: (state, action) => {state.uiState.filterMenuOpen = action.payload},
        setShadeByLayer: (state, action) => {state.uiState.shadeByLayer = action.payload},
        setShadeBy: (state, action) => {state.uiState.shadeBy = action.payload},
        setShadeByOptions: (state, action) => {state.uiState.shadeByOptions = action.payload},        
        setWarnings: (state, action) => {state.uiState.warnings = action.payload},  
        addInfo:(state, action) => {
            const general = state.uiState.info.General;
            const newGeneral = [];
            for (let i = 0; i<general.length; i++) {
                if (general[i] && general[i].hasOwnProperty('key')) {
                    if (general[i].key!==action.payload.key) {
                        newGeneral.push(general[i]);
                    }
                }
            }
            newGeneral.push(action.payload);
            state.uiState.info.General = newGeneral;
        },   
        removeInfo:(state, action) => {
            const general = state.uiState.info.General;
            const newGeneral = [];
            for (let i = 0; i<general.length; i++) {
                if (general[i] && general[i].hasOwnProperty('key')) {
                    if (general[i].key!==action.payload) {
                        newGeneral.push(general[i]);
                    }
                }
            }            
            state.uiState.info.General = newGeneral;            
        },
        addHelp:(state, action) => {
            const general = state.uiState.help.General;
            const newGeneral = general.filter((x)=>x.hasOwnProperty('key') && x['key']!==action.payload.key);
            newGeneral.push(action.payload);
            state.uiState.help.General = newGeneral;
        },
        removeHelp:(state, action) => {
            state.uiState.help.General = state.uiState.help.General.filter((x) => x.hasOwnProperty('key') && x['key']!==action.payload);
        },
        setInfo: (state, action) => {state.uiState.info = action.payload},
        setIsMobile: (state, action) => {state.uiState.isMobile = action.payload},
        setAwaitingRecenter: (state, action) => {state.uiState.awaitingRecenter = action.payload},
        setControlsVisible: (state, action) => {state.uiState.controlsVisible = action.payload},
        setGraphDialogOpen: (state, action) => {state.uiState.graphDialogOpen = action.payload},        
        setShadingValue: (state, action) => {state.uiState.shadingValue = action.payload},    
        setClickPoint: (state, action) => {state.uiState.clickPoint = action.payload},        
        setInitClickPoint: (state, action) => {state.uiState.initClickPoint = action.payload},        
        incrementLayerLoadCount: (state, action) => {
            if (state.uiState.layerLoadCount>20000) {
                state.uiState.layerLoadCount=0;
            }
            else {
                state.uiState.layerLoadCount+=1;
            }
        },
        setSideDrawerOpen: (state, action) => {state.uiState.sideDrawerOpen = action.payload},
        setTop10Order: (state, action) => {state.uiState.top10Order = action.payload},
        setTop10Category: (state, action) => {state.uiState.top10Category = action.payload},
        setTop10Params: (state, action) => {state.uiState.top10Params = action.payload},        
        setShowOverview: (state, action) => {state.uiState.showOverview = action.payload},     
        setAreaTypes: (state, action) => {state.uiState.areaTypes = action.payload},
        setPropTypes: (state, action) => {state.uiState.propTypes = action.payload},
        setMetric: (state, action) => {state.uiState.metric = action.payload},
        setShowListDialog: (state, action) => {state.uiState.showListDialog = action.payload},
        setPropertyOaPeriod: (state, action) => {state.uiState.propertyOaPeriod = action.payload},
        setPropertyOaPropType: (state, action) => {state.uiState.propertyOaPropType = action.payload},
        setPropertyOaPropTypeOptions: (state, action) => {state.uiState.propertyOaPropTypeOptions = action.payload},
        setImdCompare: (state, action) => {state.uiState.imdCompare = action.payload},
        // payload {layer, options}: merged into that layer's settings
        setAreaMapOptions: (state, action) => {
            const {layer, options} = action.payload;
            state.uiState.areaMapOptions[layer] = {...state.uiState.areaMapOptions[layer], ...options};
        },
        setAreaMapReturnLayers: (state, action) => {state.uiState.areaMapReturnLayers = action.payload},
        setPickLocationFor: (state, action) => {state.uiState.pickLocationFor = action.payload},
        setDeg: (state, action) => {state.uiState.deg = action.payload},
        setDefaultDeg: (state, action) => {state.uiState.defaultDeg = action.payload},
    }
});

export const { setPlace, setDist_m, setPoint, setLocationName, clearLocation, setSelectedLayers, setMainMenuOpen, toggleMainMenuOpen, setAlertText, 
        setSchoolType, setCrimeTypes, setPlaceDetails, setFilterMenuOpen, setShadeByLayer, setShadeBy, setShadeByOptions, setWarnings, 
        setInfo, addInfo, removeInfo, setGraphType, setPropertyType, setPropertyMaxMonths, setIsMobile, setAwaitingRecenter,
        addHelp, removeHelp, setControlsVisible, setGraphDialogOpen, setShadingValue, setClickPoint, setInitClickPoint, incrementLayerLoadCount,
        setSideDrawerOpen, setTop10Order, setTop10Category, setTop10Params, setShowOverview, setAreaTypes, setPropTypes, setMetric,
        setShowListDialog, setTransportType, setAmenityFilter, setPropertyOaPeriod, setPropertyOaPropType, setPropertyOaPropTypeOptions, setImdCompare, setAreaMapOptions, setAreaMapReturnLayers, setPickLocationFor, setDeg, setDefaultDeg } = mapSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
//export const selectMap = (state) => state.map.map;
//export const selectPopupOverlay = (state) => state.map.popupOverlay;
//export const selectPopupContent = (state) => state.map.popupContent;
//export const selectPostcode = (state) => state.map.userLocation.postcode;
//export const selectDist_m = (state) => state.map.userLocation.dist_m;
export const selectUserLocation = (state) => state.map.userLocation;
export const selectPlace = (state) => state.map.userLocation.place;
export const selectPoint = (state) => state.map.userLocation.point;
export const selectLocationName = (state) => state.map.userLocation.name;
// The name of the point now chosen, or '' while it has none (or is still being named)
export const selectCurrentLocationName = (state) => {
    const {point, name} = state.map.userLocation;
    return point && point.x && point.y && name && name.x===point.x && name.y===point.y ? name.name : '';
};
// The same, as {name, category} when it was picked from the search (category null for a
// postcode, missing when it wasn't picked), or null
export const selectCurrentLocation = (state) => {
    const {point, name} = state.map.userLocation;
    return point && point.x && point.y && name && name.x===point.x && name.y===point.y ? name : null;
};
export const selectDist_m = (state) => state.map.userOptions.dist_m;
export const selectSelectedLayers = (state) => state.map.userOptions.selectedLayers;
export const selectMainMenuOpen = (state) => state.map.uiState.mainMenuOpen;
export const selectAlertText = (state) => state.map.uiState.alertText;
export const selectCrimeTypes = (state) => state.map.uiState.crimeTypes;
export const selectSchoolType = (state) => state.map.uiState.schoolType;
export const selectTransportType = (state) => state.map.uiState.transportType;
export const selectAmenityCategories = (state) => state.map.uiState.amenityCategories;
export const selectAmenitySubcategories = (state) => state.map.uiState.amenitySubcategories;
export const selectGraphType = (state) => state.map.uiState.graphType;
export const selectPlaceDetails = (state) => state.map.uiState.placeDetails;
export const selectFilterMenuOpen = (state) => state.map.uiState.filterMenuOpen;
export const selectShadeByLayer = (state) => state.map.uiState.shadeByLayer;
export const selectShadeBy = (state) => state.map.uiState.shadeBy;
export const selectShadeByOptions = (state) => state.map.uiState.shadeByOptions;
export const selectWarnings = (state) => state.map.uiState.warnings;
export const selectInfo = (state) => state.map.uiState.info;
export const selectPropertyType = (state) => state.map.uiState.propertyType;
export const selectPropertyMaxMonths = (state) => state.map.uiState.propertyMaxMonths;
export const selectIsMobile = (state) => state.map.uiState.isMobile;
export const selectAwaitingRecenter = (state) => state.map.uiState.awaitingRecenter;
export const selectHelp = (state) => state.map.uiState.help;
export const selectControlsVisible = (state) => state.map.uiState.controlsVisible;
export const selectGraphDialogOpen = (state) => state.map.uiState.graphDialogOpen;
export const selectShadingValue = (state) => state.map.uiState.shadingValue;
export const selectClickPoint = (state) => state.map.uiState.clickPoint;
export const selectInitClickPoint = (state) => state.map.uiState.initClickPoint;
export const selectLayerLoadCount = (state) => state.map.uiState.layerLoadCount;
export const selectSideDrawerOpen = (state) => state.map.uiState.sideDrawerOpen;
export const selectTop10Order = (state) => state.map.uiState.top10Order;
export const selectTop10Category = (state) => state.map.uiState.top10Category; 
export const selectTop10Params = (state) => state.map.uiState.top10Params;
export const selectShowOverview = (state) => state.map.uiState.showOverview;
export const selectPropTypes = (state) => state.map.uiState.propTypes;
export const selectAreaTypes = (state) => state.map.uiState.areaTypes;
export const selectMetric = (state) => state.map.uiState.metric;
export const selectShowListDialog = (state) => state.map.uiState.showListDialog;
export const selectPropertyOaPeriod = (state) => state.map.uiState.propertyOaPeriod;
export const selectPropertyOaPropType = (state) => state.map.uiState.propertyOaPropType;
export const selectPropertyOaPropTypeOptions = (state) => state.map.uiState.propertyOaPropTypeOptions;
export const selectImdCompare = (state) => state.map.uiState.imdCompare;
export const selectAreaMapOptions = (state) => state.map.uiState.areaMapOptions;
export const selectAreaMapReturnLayers = (state) => state.map.uiState.areaMapReturnLayers;
export const selectPickLocationFor = (state) => state.map.uiState.pickLocationFor;
export const selectDeg = (state) => state.map.uiState.deg;
export const selectDefaultDeg = (state) => state.map.uiState.defaultDeg;
export default mapSlice.reducer;